import type { ClientRecord, ClassificationResult, RulesConfig } from './types';

/**
 * Pure, deterministic classification function. Receives rules as data so the
 * thresholds can be updated without a code deployment (see ARCHITECTURE.md).
 *
 * Highest applicable tier wins. Reasons are accumulated for the audit trail —
 * compliance must be able to see *why* a tier was assigned, not just the tier.
 */
export function classify(
  record: Pick<
    ClientRecord,
    | 'pep_status'
    | 'sanctions_screening_match'
    | 'adverse_media_flag'
    | 'country_of_tax_residence'
    | 'client_type'
    | 'annual_income'
    | 'source_of_funds'
  >,
  rules: RulesConfig,
): ClassificationResult {
  const highReasons: string[] = [];

  if (rules.high.booleanFlags.includes('pep_status') && record.pep_status) {
    highReasons.push('Politically Exposed Person');
  }
  if (
    rules.high.booleanFlags.includes('sanctions_screening_match') &&
    record.sanctions_screening_match
  ) {
    highReasons.push('Sanctions screening match');
  }
  if (rules.high.booleanFlags.includes('adverse_media_flag') && record.adverse_media_flag) {
    highReasons.push('Adverse media flag');
  }
  if (rules.high.countries.includes(record.country_of_tax_residence)) {
    highReasons.push(`High-risk jurisdiction: ${record.country_of_tax_residence}`);
  }

  if (highReasons.length > 0) {
    return { tier: 'HIGH', reasons: highReasons, rulesVersion: rules.version };
  }

  const mediumReasons: string[] = [];

  if (rules.medium.clientTypes.includes(record.client_type)) {
    mediumReasons.push(`Client type: ${record.client_type}`);
  }
  if (rules.medium.countries.includes(record.country_of_tax_residence)) {
    mediumReasons.push(`Medium-risk jurisdiction: ${record.country_of_tax_residence}`);
  }
  if (
    record.annual_income > rules.medium.incomeThreshold &&
    rules.medium.sourcesOfFunds.includes(record.source_of_funds)
  ) {
    mediumReasons.push(
      `Income > ${rules.medium.incomeThreshold.toLocaleString('en-GB')} with source: ${record.source_of_funds}`,
    );
  }

  if (mediumReasons.length > 0) {
    return { tier: 'MEDIUM', reasons: mediumReasons, rulesVersion: rules.version };
  }

  return {
    tier: 'LOW',
    reasons: ['No high-risk or medium-risk triggers'],
    rulesVersion: rules.version,
  };
}
